// ============================================================
// Helpers de leitura de planilha — compartilhados pelas Funções Extras
// de upload em lote (Upload de usuários/autores, Upload de submissões).
// ============================================================
// Funções puras, sem estado: tudo que depende só do TEXTO da célula. O
// `xlsx-import.ts` entrega a matriz de strings; daqui pra frente cada página
// decide o que cada coluna significa, mas limpa e valida com as mesmas regras.

/**
 * Normaliza um cabeçalho pra comparação: minúsculas, sem acento, traços
 * unificados e espaços colapsados.
 *
 * Existe porque planilhas reais chegam com variações invisíveis — a de
 * referência dos autores traz hífen na coluna A ("Nome - Contato") e
 * TRAVESSÃO nas colunas B e C ("Sobrenome – Contato", U+2013). Comparar
 * string literal reprovaria um arquivo visualmente correto.
 */
export function normalizeHeader(value: string): string {
  return (value || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')   // tira acentos
    .replace(/[‐-―−]/g, '-')             // – — − → -
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Validação de e-mail deliberadamente FROUXA: só descarta o que claramente
 *  não é endereço. Quem decide de verdade é o backend na criação. */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Tira um par de aspas (retas ou curvas) que envolva o valor INTEIRO —
 * artefato comum de planilha que passou por CSV/exportação antes do Excel
 * (`'joao@x.com'`). Sem isso a validação FROUXA de e-mail deixa passar um
 * endereço que o `EmailValidator` do Django rejeita na criação (aspas não são
 * caractere válido de e-mail) — a linha prometeria uma criação que sempre
 * falharia. Só remove se o PAR estiver nas duas pontas; uma aspa isolada no
 * meio fica.
 */
export function stripWrappingQuotes(value: string): string {
  const pairs: [string, string][] = [["'", "'"], ['"', '"'], ['‘', '’'], ['“', '”']];
  for (const [open, close] of pairs) {
    if (value.length > 1 && value[0] === open && value[value.length - 1] === close) {
      return value.slice(1, -1).trim();
    }
  }
  return value;
}

/** Texto com cara de e-mail num campo que não é de e-mail = dado quase certamente
 *  digitado na coluna errada (planilha com colunas deslocadas). */
export function looksLikeEmail(value: string): boolean {
  return value.indexOf('@') >= 0;
}

/** Mesma convenção do `sidebar.component.ts`: sufixo `-plural` quando
 *  count != 1. */
export function pluralKey(base: string, count: number): string {
  return base + (count === 1 ? '' : '-plural');
}
